import React from 'react';
import { designSystem } from '@yola/ws-ui';
import {
  fieldNames,
  serviceNameValues,
  doesHasContentValues,
  OTHER_VALUE,
} from '../../../constants/common';
import TextField from '../components/text-field';
import InputField from '../components/input-field';
import RadioGroupField from '../components/radio-group-field';
import CheckboxGroupFieldWrapper from '../components/checkbox-group-field-wrapper';

const { Mark } = designSystem;

const getFieldsByServiceName = (serviceName) =>
  ({
    [serviceNameValues.BUILD_YOUR_DREAM_SITE]: [
      {
        name: fieldNames.BUSINESS_AND_PURPOSE,
        title: 'What is the nature of your business and the purpose of your website?',
        description: 'Please add as many details as possible',
        placeholder: `Example: A local florist offline store. I want to generate leads via the website, showcase my work, add an online store. I want my site’s color palette to be blue, I’d like to make it more visually appealing.`,
        rules: {
          required: 'The field is required',
        },
        Component: TextField,
      },
      {
        name: fieldNames.DOES_HAS_CONTENT,
        title: 'Do you already have content for your website?',
        Component: RadioGroupField,
        rules: {
          required: 'The field is required',
        },
        options: [
          {
            label: 'Yes',
            value: doesHasContentValues.YES,
          },
          {
            label: 'No',
            value: doesHasContentValues.NO,
          },
        ],
        subFields: {
          [doesHasContentValues.YES]: {
            name: fieldNames.CONTENT_TYPE,
            title: 'What content elements do you currently have ready for your website?',
            Component: CheckboxGroupFieldWrapper,
            rules: {
              required: 'The field is required',
            },
            options: [
              {
                label: 'Logo',
                value: 'Logo',
              },
              {
                label: (
                  <React.Fragment>
                    <b>Media</b> <Mark appearance="medium-emphasis">(images/photos/video)</Mark>
                  </React.Fragment>
                ),
                isLabelBold: false,
                value: 'Media',
              },
              {
                label: (
                  <React.Fragment>
                    <b>Text materials</b>{' '}
                    <Mark appearance="medium-emphasis">(written content, articles)</Mark>
                  </React.Fragment>
                ),
                isLabelBold: false,
                value: 'Text materials',
              },
              {
                label: 'Old website',
                value: 'Old website',
              },
              {
                label: (
                  <React.Fragment>
                    <b>Other</b> <Mark appearance="medium-emphasis">(please specify)</Mark>
                  </React.Fragment>
                ),
                isLabelBold: false,
                value: OTHER_VALUE,
              },
            ],
            subFields: {
              [OTHER_VALUE]: {
                name: fieldNames.OTHER_CONTENT_TYPE,
                placeholder: `Please, write what kind of content you have`,
                isSubField: true,
                Component: TextField,
                rules: {
                  required: 'The field is required',
                },
              },
            },
          },
        },
      },
      {
        name: fieldNames.FEATURE_AND_FUNCTIONALITY,
        title: 'What features and functionality would you like to have on your website?',
        placeholder: `Example: I want to add an online store, add call-to-action buttons, add links to my email and phone number, add a cookie consent banner…`,
        Component: TextField,
        rules: {
          required: 'The field is required',
        },
      },
    ],
    [serviceNameValues.REVAMP_YOUR_WEBSITE]: [
      {
        name: fieldNames.BUSINESS_AND_PURPOSE,
        title: 'What is your site’s purpose and what kind of content does it present?',
        description: 'Please add as many details as possible',
        placeholder: `Example: A local florist offline store. It showcases my work, has customer testimonials on it, my store’s address and phone number…`,
        Component: TextField,
        rules: {
          required: 'The field is required',
        },
      },
      {
        name: fieldNames.USER_IMPROVEMENTS,
        title: 'What would you like to improve your site with?',
        placeholder:
          'Example: I want to change my site’s color palette, make it more visually appealing. I want to add more visual content. I want to improve my website layout, add extra functionality, such as…  I want to add an online store, add call-to-action buttons, add links to my email and phone number, add a cookie consent banner…',
        Component: TextField,
        rules: {
          required: 'The field is required',
        },
      },
    ],
    [serviceNameValues.MIGRATE_YOUR_SITE_TO_YOLA]: [
      {
        name: fieldNames.OLD_WEBSITE_URL,
        title: 'Please add a link to your website:',
        placeholder: 'www.example.com',
        iconGlyph: 'link',
        Component: InputField,
        rules: {
          required: 'The field is required',
        },
      },
      {
        name: fieldNames.OLD_WEBSITE_PLATFORM,
        title: 'What platform or CMS do you want to migrate your website from?',
        Component: RadioGroupField,
        rules: {
          required: 'The field is required',
        },
        options: [
          {
            label: 'WordPress',
            value: 'WordPress',
          },
          {
            label: 'Wix',
            value: 'Wix',
          },
          {
            label: 'Squarespace',
            value: 'Squarespace',
          },
          {
            label: 'Joomla',
            value: 'Joomla',
          },
          {
            label: 'Shopify',
            value: 'Shopify',
          },
          {
            label: 'Yola Sitebuilder Classic',
            value: 'Yola Sitebuilder Classic',
          },
          {
            label: 'I don’t know',
            value: 'I don’t know',
          },
          {
            label: (
              <React.Fragment>
                <b>Other</b> <Mark appearance="medium-emphasis">(please specify)</Mark>
              </React.Fragment>
            ),
            isLabelBold: false,
            value: OTHER_VALUE,
          },
        ],
        subFields: {
          [OTHER_VALUE]: {
            name: fieldNames.OTHER_OLD_WEBSITE_PLATFORM,
            placeholder: `Please, write platform or CMS for old website`,
            isSubField: true,
            Component: TextField,
            rules: {
              required: 'The field is required',
            },
          },
        },
      },
      {
        name: fieldNames.MIGRATION_REASON,
        title: 'Why do you want to migrate from your existing platform?',
        description: 'Please select all that apply',
        Component: CheckboxGroupFieldWrapper,
        rules: {
          required: 'The field is required',
        },
        options: [
          {
            label: 'It’s missing some features',
            value: 'It’s missing some features',
          },
          {
            label: 'It’s too expensive',
            value: 'It’s too expensive',
          },
          {
            label: (
              <React.Fragment>
                <b>It’s hard to use</b>{' '}
                <Mark appearance="medium-emphasis">(add new content, edit existing content)</Mark>
              </React.Fragment>
            ),
            isLabelBold: false,
            value: 'It’s hard to use',
          },
          {
            label: 'It’s not stable',
            value: 'It’s not stable',
          },
          {
            label: 'It’s poorly supported or outdated',
            value: 'It’s poorly supported or outdated',
          },
          {
            label: 'Other...',
            value: OTHER_VALUE,
          },
        ],
        subFields: {
          [OTHER_VALUE]: {
            name: fieldNames.OTHER_MIGRATION_REASON,
            placeholder: `Please, specify your reason`,
            isSubField: true,
            Component: TextField,
            rules: {
              required: 'The field is required',
            },
          },
        },
      },
      {
        name: fieldNames.USER_IMPROVEMENTS,
        title: 'Is there anything you’d like to change or improve while migrating the website?',
        placeholder:
          'Example: I want to change my site’s color palette, make it more visually appealing. I want to add more visual content. I want to improve my website layout, add extra functionality, such as…  I want to add an online store, add call-to-action buttons, add links to my email and phone number, add a cookie consent banner…',
        Component: TextField,
        rules: {
          required: 'The field is required',
        },
      },
    ],
  })[serviceName];

export default getFieldsByServiceName;
